import React, { useState } from 'react';

import useNavigationDebounce from '../../../shop-builder/hooks/useNavigationDebounce';
import type { Product } from '../../../shop-builder/types';
import { ProductClickSource } from '../../../../types/productLink';
import { makeProductPath } from '../../../../utils';
import './ProductCard.css';

interface ProductCardProps {
    product: Product;
    athleteSlug: string | null;
}

const PRODUCT_PLACEHOLDER = '/static/product-placeholder.png';

export function ProductCard({ product, athleteSlug }: ProductCardProps) {
    const [imageBroken, setImageBroken] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const canNavigate = useNavigationDebounce();

    const href = athleteSlug ? makeProductPath(athleteSlug, product.public_id, ProductClickSource.Shop) : null;
    const image = product.image_data;
    const isSrcset = image?.type === 'srcset';
    const isUrl = image?.type === 'url';
    const showFallback = !image || imageBroken;

    const inner = (
        <>
            <div className="ats-product-card__image">
                {!showFallback && !imageLoaded && (
                    <div className="ats-product-card__skeleton" aria-hidden="true" />
                )}
                {showFallback ? (
                    <img
                        className="ats-product-card__img"
                        src={PRODUCT_PLACEHOLDER}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                    />
                ) : isSrcset ? (
                    <picture
                        className={`ats-product-card__picture${imageLoaded ? ' ats-product-card__picture--loaded' : ''}`}
                    >
                        {image.srcsets.avif && <source srcSet={image.srcsets.avif} type="image/avif" />}
                        {image.srcsets.webp && <source srcSet={image.srcsets.webp} type="image/webp" />}
                        <img
                            className="ats-product-card__img"
                            src={image.srcsets.jpeg || undefined}
                            alt={image.alt_text || product.name}
                            loading="lazy"
                            decoding="async"
                            onLoad={() => setImageLoaded(true)}
                            onError={() => setImageBroken(true)}
                        />
                    </picture>
                ) : isUrl ? (
                    <img
                        className={`ats-product-card__img${imageLoaded ? ' ats-product-card__img--loaded' : ''}`}
                        src={image.url}
                        alt={image.alt || product.name}
                        loading="lazy"
                        decoding="async"
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageBroken(true)}
                    />
                ) : null}
            </div>
            <div className="ats-product-card__text">
                <p className="ats-product-card__title">{product.name}</p>
                {product.price && (
                    <p className="ats-product-card__price">{product.price}</p>
                )}
            </div>
        </>
    );

    if (href) {
        return (
            <a
                className="ats-product-card"
                href={href}
                aria-label={product.name}
                rel="nofollow"
                onClick={(e) => {
                    if (!canNavigate(href)) {
                        e.preventDefault();
                        return;
                    }
                    sessionStorage.setItem('nav-direction', 'forward');
                    sessionStorage.setItem('shop-back-url', window.location.href);
                }}
            >
                {inner}
            </a>
        );
    }
    return <article className="ats-product-card">{inner}</article>;
}
